import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, Plus, Trash2, Settings, Phone, ChevronDown,
  ChevronRight, UserPlus, Pencil, X, RefreshCw, Zap,
} from 'lucide-react'
import { teamsApi, usersApi } from '../../api/endpoints'
import type { Team, TeamMember, User } from '../../api/endpoints'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import Input, { Select, Textarea } from '../../components/ui/Input'
import Badge from '../../components/ui/Badge'
import EmptyState from '../../components/ui/EmptyState'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TEAM_TYPE_OPTIONS = [
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Support' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'custom', label: 'Custom' },
]

const ROUTING_STRATEGY_OPTIONS = [
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'least_busy', label: 'Least Busy' },
  { value: 'skill_based', label: 'Skill Based' },
  { value: 'random', label: 'Random' },
]

const FALLBACK_ACTION_OPTIONS = [
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'callback_request', label: 'Callback Request' },
  { value: 'overflow_team', label: 'Overflow Team' },
]

const TEAM_ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'lead', label: 'Lead' },
  { value: 'manager', label: 'Manager' },
]

const TYPE_BADGE_VARIANT: Record<string, 'info' | 'success' | 'purple' | 'warning'> = {
  sales: 'info',
  support: 'success',
  accounts: 'purple',
  custom: 'warning',
}

const AVAILABILITY_STYLES: Record<string, { dot: string; label: string }> = {
  online: { dot: 'bg-green-500', label: 'Online' },
  offline: { dot: 'bg-gray-400', label: 'Offline' },
  on_call: { dot: 'bg-orange-500', label: 'On Call' },
  break: { dot: 'bg-yellow-400', label: 'Break' },
}

// ---------------------------------------------------------------------------
// Helper: format routing strategy for display
// ---------------------------------------------------------------------------

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function TeamsPage() {
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [expandedTeamId, setExpandedTeamId] = useState<number | null>(null)

  // Fetch all teams
  const { data: teamsData, isLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.list(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => teamsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      setExpandedTeamId(null)
    },
  })

  const [seedMsg, setSeedMsg] = useState('')
  const seedMutation = useMutation({
    mutationFn: () => teamsApi.seedDefaults(),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      const d = res as Record<string, unknown>
      setSeedMsg((d.message as string) || `Created ${(d.created as string[])?.length || 0} items`)
      setTimeout(() => setSeedMsg(''), 5000)
    },
    onError: (err: Error) => setSeedMsg(`Error: ${err.message}`),
  })

  const teams: Team[] = teamsData?.teams || []

  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading teams...</div>
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your call routing teams and members</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
            <Zap size={16} /> {seedMutation.isPending ? 'Loading...' : 'Load Defaults'}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={16} /> Create Team
          </Button>
        </div>
      </div>

      {seedMsg && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-indigo-50 border border-indigo-200 text-sm text-indigo-700">
          {seedMsg}
        </div>
      )}

      {/* Teams list */}
      {teams.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No teams yet"
          description="Create your first team or load defaults to get started with Sales + Support teams and a default IVR menu."
          action={
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
                <Zap size={16} /> {seedMutation.isPending ? 'Loading...' : 'Load Defaults'}
              </Button>
              <Button onClick={() => setCreateOpen(true)}>
                <Plus size={16} /> Create Team
              </Button>
            </div>
          }
        />
      ) : (
        <div className="space-y-4">
          {teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              isExpanded={expandedTeamId === team.id}
              onToggle={() => setExpandedTeamId(expandedTeamId === team.id ? null : team.id)}
              onDelete={() => {
                if (confirm(`Delete team "${team.name}"? This cannot be undone.`)) {
                  deleteMutation.mutate(team.id)
                }
              }}
              deleteLoading={deleteMutation.isPending}
            />
          ))}
        </div>
      )}

      <CreateTeamModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Team card (collapsed + expanded detail)
// ---------------------------------------------------------------------------

function TeamCard({
  team,
  isExpanded,
  onToggle,
  onDelete,
  deleteLoading,
}: {
  team: Team
  isExpanded: boolean
  onToggle: () => void
  onDelete: () => void
  deleteLoading: boolean
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Collapsed header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <Users size={20} className="text-indigo-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{team.name}</span>
              <Badge variant={TYPE_BADGE_VARIANT[team.team_type] || 'default'}>
                {formatLabel(team.team_type)}
              </Badge>
              {!team.is_active && <Badge variant="danger">Inactive</Badge>}
            </div>
            {team.description && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{team.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="hidden sm:flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <RefreshCw size={13} />
              {formatLabel(team.routing_strategy)}
            </span>
            <span className="flex items-center gap-1.5">
              <Users size={13} />
              {team.member_count} member{team.member_count !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              {team.online_count} online
            </span>
          </div>
          {isExpanded ? (
            <ChevronDown size={18} className="text-gray-400" />
          ) : (
            <ChevronRight size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded detail */}
      {isExpanded && (
        <TeamDetail team={team} onDelete={onDelete} deleteLoading={deleteLoading} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Team detail (shown when card is expanded)
// ---------------------------------------------------------------------------

function TeamDetail({
  team,
  onDelete,
  deleteLoading,
}: {
  team: Team
  onDelete: () => void
  deleteLoading: boolean
}) {
  const [editMode, setEditMode] = useState(false)
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)

  // Fetch full team detail with members
  const { data: detailData, isLoading } = useQuery({
    queryKey: ['teams', team.id],
    queryFn: () => teamsApi.get(team.id),
  })

  const detail = detailData?.data
  const members: TeamMember[] = detail?.members || []

  return (
    <div className="border-t border-gray-200">
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">Loading team details...</div>
      ) : (
        <>
          {/* Team config section */}
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Settings size={15} className="text-gray-400" />
                Team Configuration
              </h3>
              <div className="flex items-center gap-2">
                {!editMode && (
                  <Button variant="secondary" size="sm" onClick={() => setEditMode(true)}>
                    <Pencil size={13} /> Edit
                  </Button>
                )}
                <Button variant="danger" size="sm" onClick={onDelete} loading={deleteLoading}>
                  <Trash2 size={13} /> Delete Team
                </Button>
              </div>
            </div>

            {editMode ? (
              <EditTeamForm team={detail || team} onDone={() => setEditMode(false)} />
            ) : (
              <TeamConfigDisplay team={detail || team} />
            )}
          </div>

          {/* Members section */}
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Users size={15} className="text-gray-400" />
                Members ({members.length})
              </h3>
              <Button size="sm" onClick={() => setAddMemberOpen(true)}>
                <UserPlus size={14} /> Add Member
              </Button>
            </div>

            {members.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">No members in this team yet.</p>
                <Button size="sm" className="mt-3" onClick={() => setAddMemberOpen(true)}>
                  <UserPlus size={14} /> Add First Member
                </Button>
              </div>
            ) : (
              <MembersTable
                teamId={team.id}
                members={members}
                onEditMember={setEditingMember}
              />
            )}
          </div>

          {/* Availability board */}
          <div className="px-6 py-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Phone size={15} className="text-gray-400" />
              Availability Board
            </h3>

            {members.length === 0 ? (
              <p className="text-sm text-gray-500">Add members to see their availability status.</p>
            ) : (
              <AvailabilityBoard members={members} />
            )}
          </div>
        </>
      )}

      {/* Add member modal */}
      <AddMemberModal
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        teamId={team.id}
        existingUserIds={members.map((m) => m.user_id)}
      />

      {/* Edit member modal */}
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          teamId={team.id}
          onClose={() => setEditingMember(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Team config display (read-only)
// ---------------------------------------------------------------------------

function TeamConfigDisplay({ team }: { team: Team }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <ConfigItem label="Type" value={formatLabel(team.team_type)} />
      <ConfigItem label="Routing Strategy" value={formatLabel(team.routing_strategy)} />
      <ConfigItem label="Routing Priority" value={String(team.routing_priority)} />
      <ConfigItem label="Max Concurrent Calls" value={String(team.max_concurrent_calls)} />
      <ConfigItem label="Fallback Action" value={team.fallback_action ? formatLabel(team.fallback_action) : 'None'} />
      <ConfigItem label="Fallback Team" value={team.fallback_team_name || 'None'} />
    </div>
  )
}

function ConfigItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Edit team form (inline)
// ---------------------------------------------------------------------------

function EditTeamForm({ team, onDone }: { team: Team; onDone: () => void }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: team.name,
    description: team.description || '',
    team_type: team.team_type,
    routing_strategy: team.routing_strategy,
    routing_priority: String(team.routing_priority),
    max_concurrent_calls: String(team.max_concurrent_calls),
    fallback_action: team.fallback_action || 'voicemail',
  })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => teamsApi.update(team.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      onDone()
    },
    onError: (err: Error) => setError(err.message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    mutation.mutate({
      name: form.name,
      description: form.description || null,
      team_type: form.team_type,
      routing_strategy: form.routing_strategy,
      routing_priority: Number(form.routing_priority),
      max_concurrent_calls: Number(form.max_concurrent_calls),
      fallback_action: form.fallback_action,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Team Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          required
        />
        <Select
          label="Team Type"
          options={TEAM_TYPE_OPTIONS}
          value={form.team_type}
          onChange={(e) => setForm((f) => ({ ...f, team_type: e.target.value }))}
        />
        <Select
          label="Routing Strategy"
          options={ROUTING_STRATEGY_OPTIONS}
          value={form.routing_strategy}
          onChange={(e) => setForm((f) => ({ ...f, routing_strategy: e.target.value }))}
        />
        <Input
          label="Routing Priority"
          type="number"
          value={form.routing_priority}
          onChange={(e) => setForm((f) => ({ ...f, routing_priority: e.target.value }))}
          min={0}
        />
        <Input
          label="Max Concurrent Calls"
          type="number"
          value={form.max_concurrent_calls}
          onChange={(e) => setForm((f) => ({ ...f, max_concurrent_calls: e.target.value }))}
          min={1}
        />
        <Select
          label="Fallback Action"
          options={FALLBACK_ACTION_OPTIONS}
          value={form.fallback_action}
          onChange={(e) => setForm((f) => ({ ...f, fallback_action: e.target.value }))}
        />
      </div>

      <Textarea
        label="Description"
        value={form.description}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        placeholder="Optional team description"
      />

      <div className="flex items-center gap-2 pt-1">
        <Button type="submit" size="sm" loading={mutation.isPending}>Save Changes</Button>
        <Button type="button" variant="secondary" size="sm" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Members table
// ---------------------------------------------------------------------------

function MembersTable({
  teamId,
  members,
  onEditMember,
}: {
  teamId: number
  members: TeamMember[]
  onEditMember: (m: TeamMember) => void
}) {
  const queryClient = useQueryClient()

  const removeMutation = useMutation({
    mutationFn: (userId: number) => teamsApi.removeMember(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
    },
  })

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Team Role</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Availability</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Skills</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Active Calls</th>
            <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {members.map((m) => {
            const avail = AVAILABILITY_STYLES[m.availability] || AVAILABILITY_STYLES.offline
            return (
              <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <span className="font-medium text-gray-900">{m.user_name}</span>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={m.team_role === 'manager' ? 'purple' : m.team_role === 'lead' ? 'info' : 'default'}>
                    {formatLabel(m.team_role)}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={`w-2.5 h-2.5 rounded-full ${avail.dot}`} />
                    {avail.label}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {m.skills.length > 0
                      ? m.skills.map((s) => <Badge key={s} variant="default">{s}</Badge>)
                      : <span className="text-xs text-gray-400">--</span>
                    }
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-sm font-medium ${m.active_call_count > 0 ? 'text-indigo-600' : 'text-gray-500'}`}>
                    {m.active_call_count}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEditMember(m)}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
                      title="Edit member"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Remove ${m.user_name} from this team?`)) {
                          removeMutation.mutate(m.user_id)
                        }
                      }}
                      disabled={removeMutation.isPending}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                      title="Remove member"
                    >
                      <X size={12} /> Remove
                    </button>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Availability board
// ---------------------------------------------------------------------------

function AvailabilityBoard({ members }: { members: TeamMember[] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {members.map((m) => {
        const avail = AVAILABILITY_STYLES[m.availability] || AVAILABILITY_STYLES.offline
        return (
          <div
            key={m.id}
            className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-center"
          >
            <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center mx-auto mb-2 relative">
              <span className="text-sm font-semibold text-gray-600">
                {m.user_name.charAt(0).toUpperCase()}
              </span>
              <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-gray-50 ${avail.dot}`} />
            </div>
            <p className="text-xs font-medium text-gray-900 truncate">{m.user_name}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{avail.label}</p>
            {m.active_call_count > 0 && (
              <p className="text-[11px] text-indigo-600 font-medium mt-0.5">
                {m.active_call_count} call{m.active_call_count !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Create Team modal
// ---------------------------------------------------------------------------

function CreateTeamModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: '',
    description: '',
    team_type: 'sales',
    routing_strategy: 'round_robin',
    max_concurrent_calls: '5',
    fallback_action: 'voicemail',
  })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => teamsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      setForm({ name: '', description: '', team_type: 'sales', routing_strategy: 'round_robin', max_concurrent_calls: '5', fallback_action: 'voicemail' })
      setError('')
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!form.name.trim()) {
      setError('Please enter a team name.')
      return
    }
    mutation.mutate({
      name: form.name.trim(),
      description: form.description.trim() || null,
      team_type: form.team_type,
      routing_strategy: form.routing_strategy,
      max_concurrent_calls: Number(form.max_concurrent_calls) || 5,
      fallback_action: form.fallback_action,
    })
  }

  return (
    <Modal open={open} onClose={onClose} title="Create Team" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

        <Input
          label="Team Name *"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Inbound Sales, Premium Support"
          required
        />

        <Textarea
          label="Description"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="What does this team handle?"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Team Type"
            options={TEAM_TYPE_OPTIONS}
            value={form.team_type}
            onChange={(e) => setForm((f) => ({ ...f, team_type: e.target.value }))}
          />
          <Select
            label="Routing Strategy"
            options={ROUTING_STRATEGY_OPTIONS}
            value={form.routing_strategy}
            onChange={(e) => setForm((f) => ({ ...f, routing_strategy: e.target.value }))}
          />
          <Input
            label="Max Concurrent Calls"
            type="number"
            value={form.max_concurrent_calls}
            onChange={(e) => setForm((f) => ({ ...f, max_concurrent_calls: e.target.value }))}
            min={1}
          />
          <Select
            label="Fallback Action"
            options={FALLBACK_ACTION_OPTIONS}
            value={form.fallback_action}
            onChange={(e) => setForm((f) => ({ ...f, fallback_action: e.target.value }))}
          />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
          <p className="font-semibold mb-1">Routing Strategies</p>
          {form.routing_strategy === 'round_robin' && <p>Distributes calls evenly across all available members in order.</p>}
          {form.routing_strategy === 'least_busy' && <p>Routes to the member handling the fewest active calls.</p>}
          {form.routing_strategy === 'skill_based' && <p>Matches incoming calls to members with the most relevant skills.</p>}
          {form.routing_strategy === 'random' && <p>Picks a random available member for each call.</p>}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending}>Create Team</Button>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Add Member modal
// ---------------------------------------------------------------------------

function AddMemberModal({
  open,
  onClose,
  teamId,
  existingUserIds,
}: {
  open: boolean
  onClose: () => void
  teamId: number
  existingUserIds: number[]
}) {
  const queryClient = useQueryClient()
  const [userId, setUserId] = useState('')
  const [teamRole, setTeamRole] = useState('member')
  const [skillsInput, setSkillsInput] = useState('')
  const [error, setError] = useState('')

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: open,
  })

  const availableUsers: User[] = (usersData?.users || []).filter(
    (u) => u.is_active && !existingUserIds.includes(u.id),
  )

  const mutation = useMutation({
    mutationFn: (data: { user_id: number; team_role?: string; skills?: string[] }) =>
      teamsApi.addMember(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      setUserId('')
      setTeamRole('member')
      setSkillsInput('')
      setError('')
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!userId) {
      setError('Please select a user.')
      return
    }
    const skills = skillsInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    mutation.mutate({
      user_id: Number(userId),
      team_role: teamRole,
      skills: skills.length > 0 ? skills : undefined,
    })
  }

  return (
    <Modal open={open} onClose={onClose} title="Add Team Member">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">User *</label>
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required
          >
            <option value="" disabled>Select a user...</option>
            {availableUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.username} ({u.role.replace('_', ' ')})
              </option>
            ))}
          </select>
          {availableUsers.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">All active users are already in this team.</p>
          )}
        </div>

        <Select
          label="Team Role"
          options={TEAM_ROLE_OPTIONS}
          value={teamRole}
          onChange={(e) => setTeamRole(e.target.value)}
        />

        <Input
          label="Skills"
          value={skillsInput}
          onChange={(e) => setSkillsInput(e.target.value)}
          placeholder="e.g. billing, storepecker, upgrades (comma-separated)"
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending} disabled={availableUsers.length === 0}>
            Add Member
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Edit Member modal
// ---------------------------------------------------------------------------

function EditMemberModal({
  member,
  teamId,
  onClose,
}: {
  member: TeamMember
  teamId: number
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [teamRole, setTeamRole] = useState(member.team_role)
  const [skillsInput, setSkillsInput] = useState(member.skills.join(', '))
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => teamsApi.updateMember(teamId, member.user_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const skills = skillsInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    mutation.mutate({
      team_role: teamRole,
      skills,
    })
  }

  return (
    <Modal open={true} onClose={onClose} title={`Edit Member — ${member.user_name}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">Editing</p>
          <p className="text-sm font-medium text-gray-900">{member.user_name}</p>
        </div>

        <Select
          label="Team Role"
          options={TEAM_ROLE_OPTIONS}
          value={teamRole}
          onChange={(e) => setTeamRole(e.target.value)}
        />

        <Input
          label="Skills"
          value={skillsInput}
          onChange={(e) => setSkillsInput(e.target.value)}
          placeholder="e.g. billing, storepecker, upgrades (comma-separated)"
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending}>Save Changes</Button>
        </div>
      </form>
    </Modal>
  )
}
